/** Visual tone for status in list + receipt (send / request / fund-request galleries). */
export type StatusTone =
  | "initial"
  | "progress"
  | "completed"
  | "failed"
  | "expired";

export type StatusPresentation = {
  tone: StatusTone;
  labelKey: string;
};

/** Hex colors for status tones (list text+icon, receipt text). */
export const STATUS_TONE_COLOR: Record<StatusTone, string> = {
  initial: "#F28D19",
  progress: "#252525",
  completed: "#31C859",
  failed: "#FF3B30",
  expired: "#8a8a8a",
};

/** Status labels/icons use the tone palette, never the partner mesh/gradient. */
export const resolveStatusToneColor = (
  tone: StatusTone,
  _brandGreen?: string,
): string => STATUS_TONE_COLOR[tone];

/**
 * Maps raw backend status, TransactionsStatus display strings, or request
 * statuses to tone + i18n key under `receipt.*`.
 */
export const toStatusPresentation = (
  status: string | undefined
): StatusPresentation => {
  switch ((status ?? "").toLowerCase()) {
    case "created":
    case "pending":
      return { tone: "initial", labelKey: "receipt.statusPending" };
    case "in_review":
      return { tone: "initial", labelKey: "receipt.statusInReview" };
    case "completed":
    case "funded":
    case "paid":
      return { tone: "completed", labelKey: "receipt.statusCompleted" };
    case "failed":
    case "rejected":
    case "error":
      return { tone: "failed", labelKey: "receipt.statusFailed" };
    case "cancelled":
    case "canceled":
      return { tone: "failed", labelKey: "receipt.statusCancelled" };
    case "expired":
      return { tone: "expired", labelKey: "receipt.statusExpired" };
    case "processing":
      return { tone: "progress", labelKey: "receipt.statusProcessing" };
    case "accepted":
      return { tone: "progress", labelKey: "receipt.statusAccepted" };
    default:
      return { tone: "progress", labelKey: "receipt.statusProcessing" };
  }
};

/** Request-domain status → presentation (when no live operation). */
export const requestToStatusPresentation = (
  status: string | undefined
): StatusPresentation => {
  switch ((status ?? "").toLowerCase()) {
    case "pending":
      return { tone: "initial", labelKey: "receipt.statusPending" };
    case "accepted":
      return { tone: "progress", labelKey: "receipt.statusAccepted" };
    case "funded":
      return { tone: "completed", labelKey: "receipt.statusFunded" };
    case "failed":
      return { tone: "failed", labelKey: "receipt.statusFailed" };
    case "cancelled":
    case "canceled":
      return { tone: "failed", labelKey: "receipt.statusCancelled" };
    case "expired":
      return { tone: "expired", labelKey: "receipt.statusExpired" };
    default:
      return { tone: "initial", labelKey: "receipt.statusPending" };
  }
};

/** Deferred binding-state → visual tone (labels stay flow-specific i18n). */
export const deferredBindingTone = (
  bindingStatus: string | undefined
): StatusTone => {
  switch ((bindingStatus ?? "").toLowerCase()) {
    case "awaiting_claim":
    case "payment_bound":
      return "initial";
    case "payout_bound":
    case "executable":
      return "progress";
    case "funded":
    case "completed":
      return "completed";
    case "failed":
    case "ofac_rejected":
    case "cancelled":
    case "canceled":
      return "failed";
    case "expired":
      return "expired";
    default:
      return "initial";
  }
};
